import { createContext, useContext, type ReactNode } from "react";

import { IconPlus, IconX } from "@/components/Icons";

import { Button } from "./controls";
import styles from "./kit.module.css";

/**
 * An ordered list of things someone declared.
 *
 * Nearly the whole sport editor is this shape — fields, kinds of fixture,
 * configs, levels — so it is a primitive rather than something each section
 * improvises.
 *
 * The order is load-bearing. It is the only layout instruction the generator
 * ever gets: the list here *is* the form an operator fills in later, in this
 * order. So moving a row is a first-class action shown on every row, not a
 * drag handle that has to be discovered.
 *
 * The handlers ride on context so a call site can write `<Row index={i}>` and
 * nothing else. Threading three callbacks through every row of every section
 * was the alternative, and it read like plumbing rather than like a list.
 */

type RowsControl = {
  count: number;
  onMove: (from: number, to: number) => void;
  onRemove: (index: number) => void;
};

const Control = createContext<RowsControl | null>(null);

export function Rows({
  children,
  empty,
  count,
  onAdd,
  addLabel,
  onMove,
  onRemove,
}: {
  children: ReactNode;
  empty: string;
  count: number;
  onAdd?: () => void;
  addLabel?: string;
  onMove: (from: number, to: number) => void;
  onRemove: (index: number) => void;
}) {
  return (
    <Control.Provider value={{ count, onMove, onRemove }}>
      <div className={styles.rows}>
        {count === 0 && <p className={styles.rowsEmpty}>{empty}</p>}
        {children}
        {onAdd && (
          <div className={styles.rowsFoot}>
            <Button onClick={onAdd}>
              <IconPlus size={15} />
              {addLabel ?? "Add"}
            </Button>
          </div>
        )}
      </div>
    </Control.Provider>
  );
}

export function Row({
  index,
  badge,
  children,
}: {
  index: number;
  /** Shown in the row's header — the code a name derives into, usually. It used
   *  to sit at the *bottom* of the card, which is the last place anyone looks for
   *  the identity of the thing they are filling in. */
  badge?: ReactNode;
  children: ReactNode;
}) {
  const control = useContext(Control);
  if (control === null) {
    throw new Error("<Row> belongs inside <Rows>");
  }
  const { count, onMove, onRemove } = control;
  return (
    <div className={styles.row}>
      {/* A header, so the position, the identity and the controls are all at the
          top where a card's chrome belongs — and so the fields below can be
          top-aligned without the controls floating in the middle of them. */}
      <div className={styles.rowHead}>
        <span className={styles.rowNum}>{index + 1}</span>
        {badge}
        <span className={styles.rowHeadGap} />
        <button
          type="button"
          className={styles.rowMove}
          aria-label="Move up"
          disabled={index === 0}
          onClick={() => onMove(index, index - 1)}
        >
          ↑
        </button>
        <button
          type="button"
          className={styles.rowMove}
          aria-label="Move down"
          disabled={index === count - 1}
          onClick={() => onMove(index, index + 1)}
        >
          ↓
        </button>
        <button
          type="button"
          className={styles.rowMove}
          aria-label="Remove"
          onClick={() => onRemove(index)}
        >
          <IconX size={14} />
        </button>
      </div>
      <div className={styles.rowBody}>{children}</div>
    </div>
  );
}
